// ============================================================
// Pay Rocky - App.jsx
// Root component: wraps all context providers and defines routes.
// ============================================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './context/AuthContext';
import { RatesProvider } from './context/RatesContext';
import { OrdersProvider } from './context/OrdersContext';
import { WalletsProvider } from './context/WalletsContext';
import { BankSettingsProvider } from './context/BankSettingsContext';

import { ProtectedRoute, AdminRoute, GuestRoute, PasswordRestrictedRoute } from './components/RouteGuards';
import ErrorBoundary from './components/ErrorBoundary';

import HomePage from './pages/HomePage';
import BuyPage from './pages/BuyPage';
import SellPage from './pages/SellPage';
import RatesPage from './pages/RatesPage';
import FAQPage from './pages/FAQPage';
import { ContactPage, PrivacyPolicyPage, TermsPage } from './pages/InfoPages';
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/AuthPages';

import UserDashboard from './pages/user/UserDashboard';
import OrderDetailsPage from './pages/user/OrderDetailsPage';
import AdminDashboard from './pages/admin/AdminDashboard';

import './styles/global.css';

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <RatesProvider>
          <WalletsProvider>
            <BankSettingsProvider>
              <OrdersProvider>
                <Router>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/buy" element={<PasswordRestrictedRoute><BuyPage /></PasswordRestrictedRoute>} />
                    <Route path="/sell" element={<PasswordRestrictedRoute><SellPage /></PasswordRestrictedRoute>} />
                    <Route path="/rates" element={<RatesPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-and-conditions" element={<TermsPage />} />

                    <Route
                      path="/login"
                      element={<GuestRoute><LoginPage /></GuestRoute>}
                    />
                    <Route
                      path="/signup"
                      element={<GuestRoute><SignupPage /></GuestRoute>}
                    />
                    <Route
                      path="/forgot-password"
                      element={<GuestRoute><ForgotPasswordPage /></GuestRoute>}
                    />
                    <Route
                      path="/reset-password/:token"
                      element={<ResetPasswordPage />}
                    />
                    <Route
                      path="/verify-email/:token"
                      element={<VerifyEmailPage />}
                    />

                    <Route
                      path="/dashboard"
                      element={<ProtectedRoute><UserDashboard /></ProtectedRoute>}
                    />
                    <Route
                      path="/dashboard/orders/:orderId"
                      element={<ProtectedRoute><OrderDetailsPage /></ProtectedRoute>}
                    />

                    <Route
                      path="/admin"
                      element={<AdminRoute><AdminDashboard /></AdminRoute>}
                    />
                    <Route
                      path="/admin/*"
                      element={<AdminRoute><AdminDashboard /></AdminRoute>}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Router>

                <Toaster
                  position="top-right"
                  gutter={10}
                  toastOptions={{
                    duration: 3200,
                    removeDelay: 250,
                    style: {
                      background: '#ffffff',
                      color: '#2c1813',
                      border: '1px solid rgba(105, 61, 43, 0.12)',
                      borderRadius: '10px',
                      boxShadow: '0 18px 45px rgba(105, 61, 43, 0.12)',
                      fontSize: '13px',
                      fontFamily: 'var(--font-body)',
                    },
                    success: {
                      duration: 2600,
                      iconTheme: { primary: '#8f3b2e', secondary: '#ffffff' },
                    },
                    error: {
                      duration: 4200,
                      iconTheme: { primary: '#b03a30', secondary: '#ffffff' },
                    },
                  }}
                />
              </OrdersProvider>
            </BankSettingsProvider>
          </WalletsProvider>
        </RatesProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
